import './home.css';
import Hero from './sections/Hero.jsx';
import TrustBar from './sections/TrustBar.jsx';
import Lifecycle from './sections/Lifecycle.jsx';
import Features from './sections/Features.jsx';
import AppReviews from './sections/AppReviews.jsx';
import Setup from './sections/Setup.jsx';
import Integrations from './sections/Integrations.jsx';
import CaseStudies from './sections/CaseStudies.jsx';
import RoiCalc from './sections/RoiCalc.jsx';
import FinalCTA from './sections/FinalCTA.jsx';
import useDocumentMeta from './hooks/useDocumentMeta.js';
import { REVIEW_COUNT, REVIEW_RATING } from './data/reviews.js';

// The SoftwareApplication + AggregateRating schema lives ONLY here, on the
// homepage — the page that actually presents the product and its reviews.
// (It used to sit in the global shell in index.html, which put a self-serving
// rating on every page, incl. Privacy/Terms — a Google review-snippet policy
// risk.) Count and rating come from src/data/reviews.js so schema can no longer
// drift from the visible copy.
const SOFTWARE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': 'https://accounteditor.com/#software',
  name: 'Account Editor',
  operatingSystem: 'Web (Shopify)',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Shopify Post-Purchase App',
  url: 'https://accounteditor.com/',
  downloadUrl: 'https://apps.shopify.com/account-editor',
  description:
    'Self-serve order editing, cancel deflection, and post-purchase upsells for Shopify. Customers update shipping, swap variants, cancel, or add items after checkout — without contacting support.',
  featureList: [
    'Self-serve shipping address changes',
    'Self-serve variant and quantity edits',
    'Self-serve cancellation with refund automation',
    'Post-purchase one-click upsells',
    'Cancel deflection with store credit and SKU swaps',
    'Address validation at checkout',
    'Configurable edit windows',
    'Shipping and tax recalculation',
    'Shopify Flow integration',
    '40+ 3PL/OMS/ERP integrations'
  ],
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: '0',
    highPrice: '179',
    offerCount: '4',
    availability: 'https://schema.org/InStock',
    url: 'https://accounteditor.com/pricing',
    offers: [
      {
        '@type': 'Offer',
        name: 'Free forever',
        description:
          'Free plan for any order volume — 25 order edits per month, basic cancel deflection, address suggestions, and one-click upsells.',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://apps.shopify.com/account-editor'
      },
      {
        '@type': 'Offer',
        name: 'Starter',
        description:
          'For Shopify stores with 0–1,200 orders per month. Unlimited edits, full feature access, email + chat support.',
        price: '39',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://accounteditor.com/pricing'
      },
      {
        '@type': 'Offer',
        name: 'Most popular',
        description:
          'For Shopify stores with 1,201–5,000 orders per month. Unlimited edits, priority support with 24-hour response SLA.',
        price: '99',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://accounteditor.com/pricing'
      },
      {
        '@type': 'Offer',
        name: 'Growth',
        description:
          'For Shopify stores with 5,001–10,000 orders per month. Unlimited edits, priority support plus a dedicated Slack channel.',
        price: '179',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        url: 'https://accounteditor.com/pricing'
      }
    ]
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: REVIEW_RATING,
    ratingCount: String(REVIEW_COUNT),
    bestRating: '5',
    worstRating: '1'
  },
  publisher: { '@id': 'https://accounteditor.com/#organization' }
};

export default function HomePage() {
  useDocumentMeta({
    title: 'Best Shopify Order Editing App | Account Editor',
    description:
      'Let Shopify customers edit orders after checkout, update details, prevent cancellations, and add products with self-service order editing.',
    path: '/',
    jsonLd: SOFTWARE_JSONLD
  });

  return (
    <div className="aehome">
      <Hero />
      <TrustBar />
      <Lifecycle />
      <Features />
      <AppReviews />
      <Setup />
      <Integrations />
      <CaseStudies />
      <RoiCalc />
      <FinalCTA />
    </div>
  );
}
